import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { INameId } from 'constants/globalTypes'
import { endOfDay, startOfDay } from 'date-fns'
import { toast } from 'react-toastify'
import { IPaginatedResponse } from 'types/api/general.interface'
import {
  IAIChatMessagesSingleReponse,
  IGlobalViewAreaPatchRequest,
  IGlobalViewAreaPostRequest,
  IGlobalViewAreasResponse,
  IGlobalViewMachinesReponse,
  IGlobalViewUsersResponse,
  IMachineInfo,
  IMachineTrip,
  IWorkerInfo,
  IWorkersAvailability,
} from 'types/api/globalViewApi.interface'
import { IGlobalViewSlice, IMachineHistory } from 'types/global-view.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'

const initialState: IGlobalViewSlice = {
  users: [],
  machines: [],
  isLoading: false,
  machinesLoading: false,
  workerLoading: false,
  chats: [],
  areas: [],
  totalPages: 0,
  totalCount: 0,
  totalPagesChats: 0,
  totalCountChats: 0,
  workerInfo: null,
  machineInfo: null,
  workersAvailability: null,
  machineHistory: null,
}

export const getGlobalViewUsers = createAsyncThunk(
  'globalView/users',
  async (
    { status, initialLoading }: { status: 'Free' | 'Busy' | 'Absent'; initialLoading: boolean },
    thunkAPI,
  ) => {
    try {
      const resp: AxiosResponse<IGlobalViewUsersResponse> = await baseUrl.get('global-view/users', {
        params: { status },
      })
      return { data: resp.data, initialLoading }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAIChats = createAsyncThunk(
  'globalView/aiChats',
  async (params: { page: number; limit: number; q?: string }, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<INameId>> = await baseUrl.get('assistant/chat', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getChatMessages = createAsyncThunk(
  'globalView/chatMessages',
  async (params: { id: number; limit: number; lastMessageId: string }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAIChatMessagesSingleReponse[]> = await baseUrl.get(
        'assistant/chat/' + params.id + '/message',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getWorkerInfo = createAsyncThunk(
  'globalView/getWorkerInfo',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IWorkerInfo> = await baseUrl.get('global-view/user/' + id + '/info')
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMachineInfo = createAsyncThunk(
  'globalView/getMachineInfo',
  async (id: number, thunkAPI) => {
    try {
      const [infoResp, tripResp]: [
        AxiosResponse<IMachineInfo[]>,
        AxiosResponse<{ trips: IMachineTrip[] }>,
      ] = await Promise.all([
        baseUrl.get(`global-view/machine/${id}/info`),
        baseUrl.get(`global-view/machine/${id}/trip`, {
          params: {
            dateFrom: startOfDay(new Date()).toISOString(),
            dateTo: endOfDay(new Date()).toISOString(),
          },
        }),
      ])

      return { ...infoResp.data, ...tripResp.data }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getWorkerAvailability = createAsyncThunk(
  'globalView/getWorkersAvailability',
  async (_, thunkAPI) => {
    try {
      const resp: AxiosResponse<IWorkersAvailability> = await baseUrl.get(
        '/users-availability/count',
      )
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getGlobalViewMachines = createAsyncThunk(
  'globalView/machines',
  async ({ status, initialLoading }: { status: 1 | 2; initialLoading: boolean }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGlobalViewMachinesReponse> = await baseUrl.get(
        'global-view/machines',
        { params: { status } },
      )
      return { data: resp.data, initialLoading }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getGlobalViewMachinesHistory = createAsyncThunk(
  'globalView/machinesHistory',
  async (id: number, thunkAPI) => {
    try {
      const now = new Date()

      const startOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0,
      ).toISOString()

      const endOfDay = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      ).toISOString()

      const resp: AxiosResponse<IMachineHistory> = await baseUrl.get(
        `global-view/machine/${id}/location/history`,
        {
          params: {
            dateFrom: startOfDay,
            dateTo: endOfDay,
          },
        },
      )

      return {
        locations: resp.data.locations.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        ),
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getGlobalViewAreas = createAsyncThunk(
  'globalView/areas',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGlobalViewAreasResponse> = await baseUrl.get('geofence-area', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addGlobalViewArea = createAsyncThunk(
  'globalView/addArea',
  async (area: IGlobalViewAreaPostRequest, thunkAPI) => {
    try {
      const resp = await baseUrl.post('geofence-area', area)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteGlobalViewArea = createAsyncThunk(
  'globalView/deleteArea',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('geofence-area/' + id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const updateGlobalViewArea = createAsyncThunk(
  'globalView/editArea',
  async (area: IGlobalViewAreaPatchRequest, thunkAPI) => {
    try {
      const resp = await baseUrl.patch('geofence-area/' + area.id, area)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const globalViewSlice = createSlice({
  name: 'globalViewSlice',
  initialState,
  reducers: {
    cleanGlobalView: (state) => {
      state.users = []
      state.areas = []
      state.machines = []
    },
    editUserPosition: (
      state,
      { payload }: { payload: { id: string; position: { latitude: number; longitude: number } } },
    ) => {
      const userIndex = state.users.findIndex((u) => u.id == Number(payload.id.split('-')[1]))
      state.users[userIndex].position = payload.position
    },
  },
  extraReducers: {
    [getGlobalViewUsers.pending.type]: (state, payload) => {
      if (payload.meta.arg.initialLoading) {
        state.isLoading = true
      }
    },
    [getGlobalViewUsers.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.users = payload.data.users
    },
    [getGlobalViewUsers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getWorkerAvailability.pending.type]: (state) => {
      state.isLoading = true
    },
    [getWorkerAvailability.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.workersAvailability = payload
    },
    [getWorkerAvailability.rejected.type]: (state, payload) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getWorkerInfo.pending.type]: (state) => {
      state.workerLoading = true
    },
    [getWorkerInfo.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.workerLoading = false
      state.workerInfo = payload
    },
    [getWorkerInfo.rejected.type]: (state, { payload }) => {
      state.workerLoading = false
      handleErrors(payload)
    },

    [getGlobalViewMachinesHistory.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.machineHistory = payload
    },
    [getGlobalViewMachinesHistory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getGlobalViewMachinesHistory.pending.type]: (state) => {
      state.isLoading = true
    },
    [getMachineInfo.pending.type]: (state) => {
      state.machinesLoading = true
    },
    [getMachineInfo.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.machinesLoading = false
      state.machineInfo = payload
    },
    [getMachineInfo.rejected.type]: (state, { payload }) => {
      state.machinesLoading = false
      handleErrors(payload)
    },
    [getGlobalViewMachines.pending.type]: (state, payload) => {
      if (payload.meta.arg.initialLoading) {
        state.isLoading = true
      }
    },
    [getGlobalViewMachines.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.machines = payload.data.machines
    },
    [getGlobalViewMachines.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addGlobalViewArea.pending.type]: (state) => {
      state.isLoading = true
    },
    [addGlobalViewArea.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.areas = [...state.areas, payload]
      toast.success('Area successsfully added.')
    },
    [addGlobalViewArea.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [updateGlobalViewArea.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateGlobalViewArea.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      const index = state.areas.findIndex((a) => a.id == payload.id)
      state.areas[index] = payload
      toast.success('Area successsfully edited.')
    },
    [updateGlobalViewArea.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteGlobalViewArea.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteGlobalViewArea.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.areas = state.areas.filter((a) => a.id !== payload)
      toast.success('Area successsfully deleted.')
    },
    [deleteGlobalViewArea.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getGlobalViewAreas.pending.type]: (state) => {
      state.isLoading = true
    },
    [getGlobalViewAreas.fulfilled.type]: (state: any, { payload }) => {
      state.isLoading = false
      state.areas = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getGlobalViewAreas.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAIChats.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAIChats.fulfilled.type]: (state: IGlobalViewSlice, { payload }) => {
      state.isLoading = false
      state.chats = payload.data
      state.totalCountChats = payload.totalCount
      state.totalPagesChats = payload.totalPages
    },
    [getAIChats.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { cleanGlobalView, editUserPosition } = globalViewSlice.actions
export default globalViewSlice.reducer
